import {useState} from "react";
import {Route, Routes, type RouteObject} from "react-router";
import AddRecipe from "../../features/recipes/AddRecipe";
import IngredientFilterPicker from "../../features/recipes/IngredientFilterPicker";
import InspectRecipe from "../../features/recipes/InspectRecipe";
import PotlistDrawer from "../../features/recipes/PotlistDrawer";
import type {RecipeSearch} from "../../features/recipes/recipe-search";
import RecipeList from "../../features/recipes/RecipeList";
import RecipeSearchBar from "../../features/recipes/RecipeSearchBar";
import RecipeViewPicker from "../../features/recipes/RecipeViewPicker";
import ScheduleRecipes from "../../features/recipes/ScheduleRecipes";
import {usePotlist} from "../../features/recipes/usePotItems";
import type {GroupEntry, GroupRepo, ItemId} from "../../models/GroupRepo";
import type {UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import GroupFooterNav from "../../shared/ui/GroupFooterNav";
import Header from "../../shared/ui/Header";
import {Box, Col, Row} from "../../ui/Box";
import {FloatingAreaButton} from "../../ui/Button";
import {DrawerRoutes, RouteDrawer} from "../../ui/Drawer";
import Icon from "../../ui/Icon";
import {useFitToVisualViewport} from "../../utils/use-fit-to-visual-viewport";
import EditRecipe from "./EditRecipe";

type IngredientFilter = {pickedItemIds: ItemId[]; onPick: (itemId: ItemId) => void};

const getModalRoutes = (
  groupRepo: GroupRepo,
  ingredientFilter: IngredientFilter
): RouteObject[] => [
  {
    path: "ingredient-filter",
    element: (
      <RouteDrawer backPath=".." title="Pick Ingredient">
        <IngredientFilterPicker groupRepo={groupRepo} {...ingredientFilter} />
      </RouteDrawer>
    ),
  },
  {
    path: "view",
    element: (
      <RouteDrawer backPath=".." title="Recipe Order">
        <RecipeViewPicker />
      </RouteDrawer>
    ),
  },
  {
    path: "potlist",
    element: (
      <RouteDrawer backPath=".." title="Prepare List">
        <PotlistDrawer groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "schedule",
    element: (
      <RouteDrawer backPath=".." title="Schedule Recipes">
        <ScheduleRecipes groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "add",
    element: (
      <RouteDrawer backPath=".." title="Add Recipe">
        <AddRecipe groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: ":recipeId",
    element: (
      <RouteDrawer backPath=".." title="See Recipe">
        <InspectRecipe groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
];

const RecipesRoutes = ({groupRepo, userRepo}: {groupRepo: GroupRepo; userRepo: UserRepo}) => (
  <Routes>
    <Route
      path=":recipeId/edit/*"
      element={<EditRecipe groupRepo={groupRepo} userRepo={userRepo} />}
    />

    <Route path="*" element={<GroupRecipes groupRepo={groupRepo} />} />
  </Routes>
);

// With recipes selected for cooking, the floating button leads to the prepare list instead of
// adding a recipe.
const RecipesActionButton = ({group}: {group: GroupEntry}) => {
  const potlist = usePotlist(group.id) ?? {};
  const count = Object.keys(potlist).length;
  if (count === 0) {
    return (
      <FloatingAreaButton to="add">
        <Icon name="plus" />
      </FloatingAreaButton>
    );
  }
  return (
    <FloatingAreaButton to="potlist">
      <Row sp="8" align="center">
        <Icon name="pot" />
        <Box size="16">{count}</Box>
      </Row>
    </FloatingAreaButton>
  );
};

const GroupRecipes = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const [search, setSearch] = useState<RecipeSearch | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  useFitToVisualViewport(searchFocused);
  const ingredientFilter: IngredientFilter = {
    pickedItemIds: search?.itemIds ?? [],
    // the search may have closed meanwhile, as opening the picker blurs an empty field
    onPick: (itemId) =>
      setSearch((prev) => ({
        query: prev?.query ?? "",
        itemIds: [...(prev?.itemIds ?? []), itemId],
      })),
  };
  return (
    <AppShell
      header={<Header title="Cook" />}
      // the keyboard covers the tabs anyway, and with the shell fitted above the keyboard they
      // would take room from the results
      footer={searchFocused ? null : <GroupFooterNav current="cook" group={group} />}
    >
      <Col px="12" pt="8" sp="24" pb="floatingButton">
        <RecipeList groupRepo={groupRepo} group={group} search={search} />
        <RecipeSearchBar
          search={search}
          onChange={setSearch}
          focused={searchFocused}
          onFocusChange={setSearchFocused}
          actionButton={<RecipesActionButton group={group} />}
          groupRepo={groupRepo}
        />
      </Col>
      <DrawerRoutes routes={getModalRoutes(groupRepo, ingredientFilter)} key={group.id} />
    </AppShell>
  );
};

export default RecipesRoutes;
